import { useMemo } from 'react';
import { SimpleGrid, Skeleton, Stack, useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { PAYMENT_TYPE_TO_METHOD, PaymentMethod, PaymentType } from '~/common/enums/payments.enum';
import { formatDollars } from '~/domains/common/utils/formatters';
import { Title } from '~/domains/design-system/Title';
import { PaymentTypeRadio } from '~/domains/payments/withdrawal/components/PaymentTypeRadio';

import { MAX_AEROPAY_ACCOUNTS, WIRE_DEPOSIT_MINIMUM } from '../../constants';
import { useAeropayLinkedBankAccounts } from '../../hooks/useAeropayLinkedBankAccounts';
import type { useAvailablePaymentMethods } from '../../hooks/useAvailablePaymentMethods';

type DepositMethodsProps = {
  availablePaymentMethods: ReturnType<typeof useAvailablePaymentMethods>['availablePaymentMethods'];
  bankAccountId?: string;
  paymentType: PaymentType;
  filteredPaymentTypes: PaymentType[];
  isLoading: boolean;
  onChange: (type: PaymentType, bankAccountId?: string) => void;
  isAeropayEnabled?: boolean;
  isWireDepositEnabled?: boolean;
  isEdgeBoostDepositEnabled?: boolean;
};

export function DepositMethods({
  availablePaymentMethods,
  bankAccountId,
  filteredPaymentTypes,
  isLoading,
  paymentType,
  onChange,
  isAeropayEnabled,
  isWireDepositEnabled,
  isEdgeBoostDepositEnabled,
}: DepositMethodsProps) {
  const { t } = useTranslation('payments');
  const theme = useMantineTheme();

  const { bankAccounts, isLoading: isLoadingAeropayLinkedBankAccounts } =
    useAeropayLinkedBankAccounts({ enabled: isAeropayEnabled });

  const opsDisabledPaymentTypes: Partial<Record<PaymentType, boolean>> = {
    [PaymentType.WIRE]: !isWireDepositEnabled,
    [PaymentType.EDGE_BOOST]: !isEdgeBoostDepositEnabled,
  };

  const getIsPaymentTypeDisabled = (type: PaymentType) => {
    if (type === PaymentType.AEROPAY && bankAccounts?.length >= MAX_AEROPAY_ACCOUNTS) {
      return { isDisabled: true, disabledReason: t('deposits.errors.aeropay.maxAccounts') };
    }

    if (opsDisabledPaymentTypes[type]) {
      return {
        isDisabled: true,
        disabledReason: t('deposits.tooltip.disabledPaymentMethod', {
          paymentMethod: t(`paymentTypes.${type}`),
        }),
      };
    }

    const isDisabled = availablePaymentMethods?.some(
      (item) => item.paymentMethod === PAYMENT_TYPE_TO_METHOD[type] && item.status === 'disabled'
    );

    return {
      isDisabled,
    };
  };

  const data = filteredPaymentTypes.map((type) => {
    const { isDisabled, disabledReason } = getIsPaymentTypeDisabled(type);

    return {
      type,
      settings: { isFree: true, description: '', isDisabled, disabledReason },
    };
  });

  const isAeropayVisible = useMemo(
    () =>
      isAeropayEnabled &&
      availablePaymentMethods?.some(
        (item) => item.paymentMethod === PaymentMethod.AEROPAY && item.status === 'displayed'
      ),
    [availablePaymentMethods, isAeropayEnabled]
  );

  return (
    <Stack spacing={theme.other.spacing._32}>
      {isLoadingAeropayLinkedBankAccounts ? <Skeleton height={110} /> : null}
      {isAeropayVisible && bankAccounts?.length > 0 ? (
        <Stack spacing={theme.other.spacing._16} align="center">
          <Title variant="subhead-medium">{t('deposits.savedDepositMethods')}</Title>
          <Stack spacing={theme.other.spacing._8} w="100%">
            {bankAccounts?.map((bankAccount, index) => (
              <PaymentTypeRadio
                key={bankAccount.bankAccountId}
                type={PaymentType.AEROPAY}
                value={bankAccount.bankAccountId}
                settings={{ isFree: true, isDisabled: false }}
                isActive={bankAccountId === bankAccount.bankAccountId}
                superscriptIndex={index + 1}
                isWithdrawal={false}
                onChange={() => {
                  onChange(PaymentType.AEROPAY, bankAccount.bankAccountId);
                }}
                bankAccount={bankAccount}
                isDeleteBankAccountVisible
              />
            ))}
          </Stack>
        </Stack>
      ) : null}

      <Stack align="center">
        {isAeropayEnabled ? (
          <>
            {(() => {
              const aeropayMethods = data.filter(({ type }) => type === PaymentType.AEROPAY);
              return !isLoadingAeropayLinkedBankAccounts ? (
                <>
                  {bankAccounts?.length > 0 && aeropayMethods.length > 0 ? (
                    <Title variant="subhead-medium">{t('deposits.connectAnotherBank')}</Title>
                  ) : null}

                  <SimpleGrid cols={1} spacing={theme.other.spacing._8} w="100%">
                    {aeropayMethods.map(({ type, settings }, index) => (
                      <PaymentTypeRadio
                        key={type}
                        type={type}
                        value={type}
                        settings={settings}
                        isActive={type === paymentType && bankAccountId === undefined}
                        superscriptIndex={index + 1}
                        isWithdrawal={false}
                        recommended={!bankAccounts?.length && !isLoadingAeropayLinkedBankAccounts}
                        description={t('deposits.recommendedDepositDescription')}
                        onChange={() => {
                          if (!settings.isDisabled) {
                            onChange(type);
                          }
                        }}
                      />
                    ))}
                  </SimpleGrid>
                </>
              ) : null;
            })()}
          </>
        ) : null}
      </Stack>

      <Stack spacing={theme.other.spacing._16} align="center">
        {isAeropayEnabled && (
          <Title variant="subhead-medium">{t('deposits.otherDepositMethods')}</Title>
        )}
        {isLoading ? <Skeleton height={310} /> : null}

        <SimpleGrid cols={2} spacing={theme.other.spacing._8} w="100%">
          {data
            .filter(({ type }) => type !== PaymentType.AEROPAY)
            .map(({ type, settings }, index) => (
              <PaymentTypeRadio
                key={type}
                type={type}
                value={type}
                settings={settings}
                isActive={type === paymentType && bankAccountId === undefined}
                superscriptIndex={index + 1}
                isWithdrawal={false}
                isNew={type === PaymentType.WHOP_CRYPTO}
                description={
                  type === PaymentType.WIRE
                    ? t('deposits.wireTransfer.tileTag', {
                        amount: formatDollars(WIRE_DEPOSIT_MINIMUM),
                      })
                    : undefined
                }
                onChange={() => {
                  if (!settings.isDisabled) {
                    onChange(type);
                  }
                }}
              />
            ))}
        </SimpleGrid>
      </Stack>
    </Stack>
  );
}
